import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState, useEffect } from 'react';
import { Button, Modal, ModalBody, Input, UncontrolledTooltip } from 'reactstrap';
import dayjs from 'dayjs';
import { faPlus, faTimes } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';

import FlexEventItem from './FlexEventItem/FlexEventItem';
import AddFlexEventModal from './AddFlexEventModal/AddFlexEventModal';
import EditFlexEventModal from './EditFlexEventModal/EditFlexEventModal';
import style from './FlexEventDetailModal.style';
import { popoverModifiers } from '../../../../../enums';

const FlexEventDetailModal = ({
  candidates,
  columnTracker,
  dateIsInThePast,
  evaluators = [],
  fireConfirmationForEditingPastSchedule,
  forcedContext,
  indexedFlexEventEvaluators,
  isOpen,
  pk_InterviewDate,
  rowTracker,
  scheduleData,
  selectedCell,
  setSelectedCell,
  setShowFlexEventDetailModal,
  showPastScheduleWarning,
  updateAllData,
  updateFlexEvents,
  willEditThePast,
  VirtualMeetingMode,
}) => {
  const [showAddFlexEventModal, setShowAddFlexEventModal] = useState(false);
  const [showEditFlexEventModal, setShowEditFlexEventModal] = useState(false);

  const [viewState, setViewState] = useState('display'); // 'display', 'massed_edit'
  const [flexEventToEdit, setFlexEventToEdit] = useState(null);
  const [FlexEvents, setFlexEvents] = useState([]);
  const [flexEventsForEdit, setFlexEventsForEdit] = useState([]);
  const [timeSlot, setTimeSlot] = useState();
  const [wasOpen, setWasOpen] = useState(false);
  const [minutesAdjustment, setMinutesAdjustment] = useState(0);

  const { metaData = [] } = scheduleData || {};

  useEffect(() => {
    if (isOpen) {
      setWasOpen(true);
      if (selectedCell) {
        const newRow = selectedCell.row;
        setTimeSlot(JSON.stringify(newRow));
        setFlexEvents(newRow.FlexEvents || []);
      } else if (!selectedCell) {
        setTimeSlot('all');
        extractAllFlexEvents();
      }
    } else {
      if (wasOpen) {
        // Only clear selected cell if the modal was open
        setSelectedCell(null);
        // Clear the flexEventsForEdit array when modal closes
        setFlexEventsForEdit([]);
      }

      setWasOpen(false);
    }
  }, [isOpen, selectedCell ? JSON.stringify(selectedCell) : null]);

  useEffect(() => {
    if (scheduleData && metaData.FlexEvents) {
      extractAllFlexEvents();
    }
  }, [metaData]);

  const saveMassEdit = async () => {
    // Validation
    if (flexEventsForEdit.length === 0) {
      Swal.fire('No Events Selected', 'Please select at least one event to modify.', 'warning');
      return;
    }

    const adjustment = parseInt(minutesAdjustment) || 0;
    if (adjustment === 0) {
      Swal.fire('No Adjustment', 'Please enter a non-zero minutes adjustment.', 'warning');
      return;
    }

    // Confirm with user
    const result = await Swal.fire({
      title: 'Confirm Mass Edit',
      text: `You are about to modify ${flexEventsForEdit.length} event(s) by ${
        adjustment > 0 ? '+' : ''
      }${adjustment} minutes. Continue?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, Update',
      cancelButtonText: 'Cancel',
    });

    if (!result.isConfirmed) return;

    // Transform flexEventsForEdit data to match updateFlexEvents expected format
    const eventsData = flexEventsForEdit.map((flexEvent) => {
      const { FlexDateStart, FlexTimeStart, FlexDateEnd, FlexTimeEnd } = flexEvent;
      const originalStart = dayjs(`${FlexDateStart} ${FlexTimeStart}`);
      const originalEnd = dayjs(`${FlexDateEnd} ${FlexTimeEnd}`);

      const newStart = originalStart.add(adjustment, 'minutes');
      const newEnd = originalEnd.add(adjustment, 'minutes');

      return {
        pk_FlexEvent: flexEvent.pk_FlexEvent,
        EventName: flexEvent.FlexEventName || flexEvent.EventName,
        FlexEventName: flexEvent.FlexEventName || flexEvent.EventName,
        FlexTimeStart: newStart.format('HH:mm:ss'),
        FlexTimeEnd: newEnd.format('HH:mm:ss'),
        EndDateTime: newEnd.toISOString(),
        evaluatorsToAdd: [], // Not modifying evaluators in mass edit
        evaluatorsToDelete: [], // Not modifying evaluators in mass edit
      };
    });

    // Call the existing updateFlexEvents function
    updateFlexEvents(eventsData, (err) => {
      if (err) {
        // Error already handled by updateFlexEvents (shows toast)
        console.error('Mass edit error:', err);
      } else {
        // Success! Clean up state and refresh all data
        setFlexEventsForEdit([]);
        setMinutesAdjustment(0);
        setViewState('display');
        updateAllData(); // Refreshes schedule and resets isGeneratingSchedule
      }
    });
  };

  const extractAllFlexEvents = () => {
    const { metaData = {}, headers = [] } = scheduleData || {};
    const schedFlexEvents = metaData.FlexEvents ? metaData.FlexEvents.slice() : [];
    const evaluatorFlexEvents = [];

    const allFlexEvents = [...schedFlexEvents];
    setFlexEvents(allFlexEvents);
  };

  const renderAddFlexEventModal = () => {
    return (
      <AddFlexEventModal
        candidates={candidates}
        columnTracker={columnTracker}
        dateIsInThePast={dateIsInThePast}
        evaluators={evaluators}
        fireConfirmationForEditingPastSchedule={fireConfirmationForEditingPastSchedule}
        forcedContext={forcedContext}
        indexedFlexEventEvaluators={indexedFlexEventEvaluators}
        isOpen={showAddFlexEventModal}
        pk_InterviewDate={pk_InterviewDate}
        rowTracker={rowTracker}
        scheduleData={scheduleData}
        selectedCell={selectedCell}
        setSelectedCell={setSelectedCell}
        selectedTimeSlot={timeSlot}
        setShowAddFlexEventModal={setShowAddFlexEventModal}
        setShowFlexEventDetailModal={setShowFlexEventDetailModal}
        updateAllData={updateAllData}
        willEditThePast={willEditThePast}
      />
    );
  };

  const renderEditFlexEventModal = () => {
    return (
      <EditFlexEventModal
        candidates={candidates}
        columnTracker={columnTracker}
        dateIsInThePast={dateIsInThePast}
        evaluators={evaluators}
        fireConfirmationForEditingPastSchedule={fireConfirmationForEditingPastSchedule}
        flexEventToEdit={flexEventToEdit}
        forcedContext={forcedContext}
        indexedFlexEventEvaluators={indexedFlexEventEvaluators}
        isOpen={showEditFlexEventModal}
        pk_InterviewDate={pk_InterviewDate}
        rowTracker={rowTracker}
        scheduleData={scheduleData}
        selectedCell={selectedCell}
        setSelectedCell={setSelectedCell}
        setShowEditFlexEventModal={setShowEditFlexEventModal}
        setShowFlexEventDetailModal={setShowFlexEventDetailModal}
        showPastScheduleWarning={showPastScheduleWarning}
        updateAllData={updateAllData}
        willEditThePast={willEditThePast}
      />
    );
  };

  const renderFlexEventsDisplay = () => {
    return (
      <div style={{ ...style.simpleColumn, height: 300, overflowY: 'scroll' }}>
        {FlexEvents && FlexEvents.length > 0 ? (
          FlexEvents.filter((FlexEvent) => {
            return (
              (FlexEvent.FlexEventName && FlexEvent.FlexEventName.toLowerCase() !== 'unavailable') ||
              (FlexEvent.EventName && FlexEvent.EventName.toLowerCase() !== 'unavailable')
            );
          }).map((FlexEvent, index) => {
            return (
              <FlexEventItem
                index={index}
                key={`flex_event_${FlexEvent.pk_FlexEvent}`}
                flexEvent={FlexEvent}
                scheduleData={scheduleData}
                VirtualMeetingMode={VirtualMeetingMode}
                setFlexEventToEdit={(newFlexEvent) => {
                  setFlexEventToEdit(newFlexEvent);
                  setShowEditFlexEventModal(true);
                }}
              />
            );
          })
        ) : (
          <div style={{ textAlign: 'center' }}>No Flex Events</div>
        )}
      </div>
    );
  };

  const renderFlexEventsMassedEdit = () => {
    return (
      <div style={{ ...style.simpleColumn, height: 300, overflowY: 'scroll' }}>
        {FlexEvents && FlexEvents.length > 0 ? (
          FlexEvents.filter((FlexEvent) => {
            return (
              (FlexEvent.FlexEventName && FlexEvent.FlexEventName.toLowerCase() !== 'unavailable') ||
              (FlexEvent.EventName && FlexEvent.EventName.toLowerCase() !== 'unavailable')
            );
          }).map((FlexEvent, index) => {
            const isSelected = flexEventsForEdit.some((item) => item.pk_FlexEvent === FlexEvent.pk_FlexEvent);

            return (
              <FlexEventItem
                index={index}
                showCheckBox={true}
                isSelected={isSelected}
                minutesAdjustment={minutesAdjustment}
                onCheckBoxChange={(e) => {
                  const isChecked = e.target.checked;

                  if (isChecked) {
                    // Add to array if checked
                    setFlexEventsForEdit((prev) => [...prev, FlexEvent]);
                  } else {
                    // Remove from array if unchecked
                    setFlexEventsForEdit((prev) => prev.filter((item) => item.pk_FlexEvent !== FlexEvent.pk_FlexEvent));
                  }
                }}
                key={`flex_event_${FlexEvent.pk_FlexEvent}`}
                flexEvent={FlexEvent}
                scheduleData={scheduleData}
                VirtualMeetingMode={VirtualMeetingMode}
              />
            );
          })
        ) : (
          <div style={{ textAlign: 'center' }}>No Flex Events</div>
        )}
      </div>
    );
  };

  const renderContent = () => {
    if (viewState === 'massed_edit') {
      return renderFlexEventsMassedEdit();
    }
    return renderFlexEventsDisplay();
  };

  return (
    <>
      <Modal isOpen={isOpen} centered={true} style={{ minWidth: '80vw' }}>
        <div
          style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', width: '100%' }}
          className="modal-header"
        >
          <div
            style={{
              width: '350px',
              flexShrink: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginRight: '10px',
              borderLeft: '1px solid #ccc',
              borderRight: '1px solid #ccc',
              paddingLeft: '10px',
              paddingRight: '10px',
              paddingTop: '2px',
              paddingBottom: '2px',
              backgroundColor: '#f5f5f5',
            }}
          >
            {viewState === 'massed_edit' ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span>minutes</span>
                <Input
                  type="number"
                  style={{ width: 100 }}
                  placeholder="Minutes"
                  value={minutesAdjustment}
                  onChange={(e) => setMinutesAdjustment(e.target.value)}
                />
                <Button
                  size="sm"
                  color="danger"
                  onClick={() => {
                    // Clear the flexEventsForEdit array when switching view states
                    setFlexEventsForEdit([]);
                    setViewState('display');
                  }}
                >
                  Cancel
                </Button>
                <Button size="sm" color="success" disabled={flexEventsForEdit.length === 0} onClick={saveMassEdit}>
                  Commit
                </Button>
              </div>
            ) : (
              <Button
                size="sm"
                color="success"
                onClick={() => {
                  // Clear the flexEventsForEdit array when switching view states
                  setFlexEventsForEdit([]);
                  setViewState('massed_edit');
                }}
              >
                Modify Multiple Event Times
              </Button>
            )}
          </div>

          <div
            style={{
              width: '350px',
              flexShrink: 0,
              textAlign: 'left',
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
            }}
          >
            <span style={{ whiteSpace: 'nowrap' }}>Viewing</span>
            <Input
              type="select"
              style={{ width: '100%' }}
              value={timeSlot}
              onChange={(e) => {
                if (e.target.value !== 'all') {
                  const row = JSON.parse(e.target.value);
                  setTimeSlot(row);
                  setFlexEvents(row.FlexEvents || []);
                } else {
                  extractAllFlexEvents();
                }

                setTimeSlot(e.target.value);
              }}
            >
              {scheduleData ? (
                <>
                  <option disabled selected>
                    Select Time Slot
                  </option>
                  <option value={'all'}>All Flex Events</option>
                  {scheduleData.body.map((row) => {
                    return (
                      <option value={JSON.stringify(row)} key={row.pk_Timeslot}>
                        {dayjs(row.slot).format('hh:mm A')} - {dayjs(row.slotEnd).format('hh:mm A')} Flex Events
                      </option>
                    );
                  })}
                </>
              ) : (
                <option disabled>No Timeslots</option>
              )}
            </Input>
          </div>

          {/* Column 4: Add Button - 40px, right aligned */}
          <div
            style={{
              width: '40px',
              flexShrink: 0,
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
            }}
          >
            <Button
              id="addFlexEventsPlus"
              size="sm"
              color="success"
              onClick={async () => {
                if (dateIsInThePast && !willEditThePast) {
                  const continueProcess = await fireConfirmationForEditingPastSchedule();
                  if (!continueProcess) {
                    return;
                  }
                }
                setShowAddFlexEventModal(true);
              }}
            >
              <FontAwesomeIcon icon={faPlus} />
              <UncontrolledTooltip target="addFlexEventsPlus" modifiers={popoverModifiers}>
                Create New Flex Event
              </UncontrolledTooltip>
            </Button>
          </div>

          {/* Close Button - outside 4-column layout */}
          <FontAwesomeIcon
            icon={faTimes}
            style={{ marginLeft: 'auto', cursor: 'pointer', flexShrink: 0 }}
            onClick={() => {
              setSelectedCell(null);
              setShowFlexEventDetailModal(false);
            }}
          />
        </div>
        <ModalBody>{renderContent()}</ModalBody>
      </Modal>
      {renderAddFlexEventModal()}
      {renderEditFlexEventModal()}
    </>
  );
};

export default FlexEventDetailModal;
